import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import useDataSuiteWidgets from './useDataSuiteWidgets';
import { tableColors, Theme } from '../../../config/constants';
import { FaExpand, FaTrash } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import { IoMdInformationCircle } from "react-icons/io";
import { BsCircleFill } from 'react-icons/bs';
import { Tooltip } from "@mui/material";
import { isEqual } from "lodash";
import formatters from '../../../config/formatters';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import EmptySearchResultsMessage from '../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage';

ChartJS.register(...registerables);

const CompetitiveInsightsWidget = (props) => {
    const { w, i } = props;
    const {  onClickWidgetDrawerOpen, onClickDeleteWidget, onClickWidgetDetailsDrawerOpen } = useDataSuite.useWidgetControls();
    const { widgetDetailsContent, competitiveInsightsTableLabels, competitiveInsightsRightTableData, competitiveInsightsRadialData, tableOptions, widgetTitle, filtersEnabled } = useDataSuiteWidgets.useCompetitiveInsightsWidget();
    const { numberFormatter } = formatters();
    const competitiveInsightsDetailsTableData = useSelector(({datasuite}) => datasuite.widgetDetailsData.competitiveInsightsData);
    const detailsDrawerTitle = useSelector(({datasuite})=> datasuite.widgetDetailsDrawer.title);
    const detailsDrawerOpen = useSelector(({ui}) => ui.datasuiteWidgetDetailsSlideDrawer);
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const selectedFilters = useSelector(({datasuite}) => datasuite.selectedFilters);
    const selectedDetailsFilters = useSelector(({datasuite}) => datasuite.selectedDetailsFilters);
    const allFilters = useSelector(({datasuite}) => datasuite.allFilters);
    const dateString = useSelector(({dates}) => dates.datasuiteDateString);
    const detailsDateString = useSelector(({dates}) => dates.datasuiteDetailsDateString);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.competitiveInsightsLoading);
    const detailsIsLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.competitiveInsightsLoading);
    const { getCompetitiveInsights } = useDataSuite.useDataSuite();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);
    const selectedManufacturers = useSelector(({user}) => user.selectedManufacturers);

    const prevFilters = useRef(null);
    const prevDate = useRef(null);
    const prevAllFilters = useRef(null);
    const prevManufacturers = useRef(null);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    useEffect(() => {
        if (isEqual(selectedFilters, prevFilters.current) &&
            isEqual(dateString, prevDate.current) &&
            isEqual(allFilters, prevAllFilters.current) &&
            isEqual(selectedManufacturers, prevManufacturers.current)
        ) {
            getCompetitiveInsights(true);
        } else {
            getCompetitiveInsights(false);
        }
        prevFilters.current = selectedFilters;
        prevDate.current = dateString;
        prevAllFilters.current = allFilters;
        prevManufacturers.current = selectedManufacturers;

    // eslint-disable-next-line
    }, [allFilters, selectedFilters, selectedDetailsFilters, dateString, detailsDateString, selectedManufacturers]);

    // eslint-disable-next-line
    useEffect(() => {if(detailsDrawerOpen && (detailsDrawerTitle === widgetTitle)) {onClickWidgetDetailsDrawerOpen(widgetTitle, widgetDetailsContent());}}, [competitiveInsightsDetailsTableData, detailsIsLoading]);

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays the total case quantity of your purchases compared to competitive MFRs in the same categories. Helpful for tracking market share and identifying growth potential.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered) && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label replaceWidgetButton'
                            size='sm'
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' />
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label deleteWidgetButton'
                            size='sm'
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                    <Button
                        block
                        className={`py-1 button-label expandWidgetButton`}
                        size='sm'
                        outline={true}
                        color={Theme().colors.accentName}
                        onClick={() => onClickWidgetDetailsDrawerOpen(widgetTitle, widgetDetailsContent())} >
                        <FaExpand color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> Expand
                    </Button>
                </Col>
            </Row>
            {(isLoading || allFilters?.categories === undefined) &&
              <Row className="mx-auto" style={{minHeight: '315px'}}>
                  <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                      <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                  </Col>
              </Row>
            }
            {(competitiveInsightsRightTableData?.length <= 0) && (!isLoading && !!allFilters?.categories) &&
            <Row className="mx-auto" style={{height: '70%'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    {(!isLoading && competitiveInsightsRightTableData?.length <= 0 && filtersEnabled) &&
                    <EmptySearchResultsMessage />
                    }
                    {(!isLoading && competitiveInsightsRightTableData?.length <= 0 && !filtersEnabled) &&
                    <EmptyDataMessage />
                    }
                </Col>
            </Row>
            }
            {(competitiveInsightsRightTableData?.length > 0) && (!isLoading && !!allFilters?.categories) &&
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 col-xl-5 p-0">
                    <Doughnut
                        data = {{
                            labels: competitiveInsightsTableLabels,
                            datasets: [
                                {
                                    label: '',
                                    data: competitiveInsightsRadialData,
                                    backgroundColor: tableColors
                                }
                            ]
                        }}
                        options = {tableOptions}
                    />
                </Col>
                <Col className='my-auto w-100 text-size-14' style={{height: "275px"}}>
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table competitiveInsightsWidgetTable border-grayLight" id="competitiveInsightsWidgetTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={competitiveInsightsRightTableData.length}
                                rowGetter={({ index }) => competitiveInsightsRightTableData[index]}
                            >
                                <Column
                                label='Manufacturer'
                                dataKey='manufacturer'
                                width={width * 0.7}
                                cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[(rowIndex) % 12]} /> {cellData}</span>)}
                                />
                                <Column
                                width={width * 0.3}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            </Row>
            }
        </Col>
    );

}

export default CompetitiveInsightsWidget;
